import { memo, useMemo, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { Phase } from '@ringmaster/shared';
import { usePhaseDuration } from '../theme/phaseTheme.js';
import type { SceneDef, SceneManifest, SceneLayer } from '../assets/scenes/types.js';
import { ParticleLayer } from './particles/ParticleLayer.js';

/**
 * SceneStack — the AAA parallax backdrop that replaces PhaseBackground's flat
 * gradient. Resolves the current Phase to a named scene in the game's
 * SceneManifest and crossfades between scenes as the phase graph advances.
 *
 * Each scene is a back-to-front stack of image layers (sky / mid / fog / fore),
 * each optionally drifting horizontally or breathing in opacity, interleaved with
 * ambient particle emitters. A scene whose `layers` is empty (art not shipped yet)
 * renders its `fallbackGradient` — so the game is fully playable before any art.
 *
 * Z-order inside the scene: sky 0, mid 1, fog-particles 2, fore 3, fore-particles
 * 4, children (the screen UI) 10.
 *
 * Crossfade duration reuses the same --duration-phase seam PhaseBackground uses,
 * so prefers-reduced-motion collapses it to instant. The AnimatePresence is keyed
 * on the resolved SCENE KEY only (not the phase), so the six night sub-phases,
 * which all map to the NIGHT scene, do NOT re-trigger a crossfade between them.
 *
 * Usage:
 *   import { scenesForGame } from '../assets/scenes';
 *   const manifest = scenesForGame('bloodmoon');
 *   <SceneStack manifest={manifest} phase={phase} winner={winner}>
 *     <StageView … />
 *   </SceneStack>
 */

export interface SceneStackProps {
  /** The game's scene manifest (stable reference). */
  manifest: SceneManifest;
  /** Current game phase. */
  phase: Phase;
  /** Winner override for the END phase (WOLVES → END_WOLVES scene). */
  winner?: 'VILLAGE' | 'WOLVES';
  /** Screen UI rendered above the backdrop (z 10). */
  children?: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

/** Resolve the scene key for a phase, applying the END + winner override. */
export function resolveSceneKey(
  manifest: SceneManifest,
  phase: Phase,
  winner?: 'VILLAGE' | 'WOLVES',
): string {
  if (phase === 'END' && winner === 'WOLVES' && manifest.scenes.END_WOLVES) {
    return 'END_WOLVES';
  }
  return manifest.phaseToScene[phase];
}

/** Base z-index for a layer's depth band. */
const SLOT_Z: Record<SceneLayer['slot'], number> = { sky: 0, mid: 1, fog: 3, fore: 3 };

function SceneStackImpl({ manifest, phase, winner, children, className, style }: SceneStackProps) {
  const duration = usePhaseDuration();
  const sceneKey = resolveSceneKey(manifest, phase, winner);
  const scene: SceneDef | undefined = manifest.scenes[sceneKey];

  // Per-phase dim over the whole scene stack so the ink-black base shows through
  // and the art stops dominating the ornaments/UI above. Undimmed (1) when a phase
  // isn't listed. This rides a STATIC inner wrapper (below), multiplying cleanly
  // with the crossfade motion.div's animated opacity and preserving the relative
  // 'screen' blend of the moon/fog layers inside the wrapper.
  const dim = manifest.phaseDim?.[phase] ?? 1;

  // Per-scene keyframe CSS (drift + pulse). Recomputed only when the scene flips.
  const keyframes = useMemo(() => (scene ? buildKeyframes(sceneKey, scene) : ''), [sceneKey, scene]);

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        height: '100%',
        ...style,
      }}
    >
      <AnimatePresence mode="sync">
        <motion.div
          key={sceneKey}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration, ease: [0.22, 1, 0.36, 1] }}
          style={{ position: 'absolute', inset: 0, zIndex: 0 }}
          aria-hidden
        >
          {/* Static dim wrapper: contains EVERY scene layer so 'screen' blend
              layers (moon/fog) composite against each other + the black sky here,
              then the whole result is dimmed by `dim` against the base. Opacity
              lives on this wrapper, NOT the animated crossfade div, so the two
              opacities multiply and the crossfade stays intact. */}
          <div style={{ position: 'absolute', inset: 0, opacity: dim }}>
          {!scene || scene.layers.length === 0 ? (
            // Zero-layer scene → flat fallback gradient (identical to today).
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: scene?.fallbackGradient ?? '#080808',
              }}
            />
          ) : (
            <>
              {scene.layers.map((layer, i) => (
                <div
                  key={`${layer.slot}-${i}`}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    // Layer art is authored ~120% width for a seamless drift loop.
                    width: layer.drift ? '120%' : '100%',
                    backgroundImage: `url(${layer.src})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    mixBlendMode: layer.blend === 'screen' ? 'screen' : 'normal',
                    opacity: layer.opacity ?? 1,
                    zIndex: SLOT_Z[layer.slot],
                    willChange: layer.drift || layer.pulse ? 'transform, opacity' : undefined,
                    // Ambient CSS animations, gated behind no-preference below.
                    animationName: [
                      layer.drift ? driftName(sceneKey, i) : '',
                      layer.pulse ? pulseName(sceneKey, i) : '',
                    ]
                      .filter(Boolean)
                      .join(', '),
                    animationDuration: [
                      layer.drift ? `${layer.drift.periodS}s` : '',
                      layer.pulse ? `${layer.pulse.periodS}s` : '',
                    ]
                      .filter(Boolean)
                      .join(', '),
                    animationTimingFunction: 'ease-in-out',
                    animationIterationCount: 'infinite',
                  }}
                />
              ))}
              {/* Ambient particle emitters, mounted per configured slot. */}
              {(scene.particles ?? []).map((p, i) => (
                <ParticleLayer
                  key={`${p.preset}-${i}`}
                  preset={p.preset}
                  intensity={p.intensity}
                  slot={p.slot === 'fore' ? 4 : 2}
                />
              ))}
            </>
          )}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Screen UI sits above the whole backdrop. */}
      <div style={{ position: 'relative', zIndex: 10, height: '100%' }}>{children}</div>

      {/* Ambient motion only when the user hasn't asked for reduced motion. */}
      {keyframes && (
        <style>{`
          @media (prefers-reduced-motion: no-preference) {
            ${keyframes}
          }
        `}</style>
      )}
    </div>
  );
}

const driftName = (key: string, i: number) => `ss-${key}-drift-${i}`;
const pulseName = (key: string, i: number) => `ss-${key}-pulse-${i}`;

/** Build the @keyframes text for a scene's drifting / pulsing layers. */
function buildKeyframes(key: string, scene: SceneDef): string {
  const rules: string[] = [];
  scene.layers.forEach((layer, i) => {
    if (layer.drift) {
      // Layer is 120% width; oscillate within the overscan so no seam shows.
      const x = layer.drift.xPx;
      rules.push(
        `@keyframes ${driftName(key, i)} {` +
          `0%,100% { transform: translateX(0); }` +
          `50% { transform: translateX(-${x}px); }` +
          `}`,
      );
    }
    if (layer.pulse) {
      const stops = layer.pulse.opacity;
      const frames = stops
        .map((o, s) => `${Math.round((s / (stops.length - 1)) * 100)}% { opacity: ${o}; }`)
        .join(' ');
      rules.push(`@keyframes ${pulseName(key, i)} { ${frames} }`);
    }
  });
  return rules.join('\n');
}

export const SceneStack = memo(SceneStackImpl);
